import { useSyncExternalStore } from "react";

/**
 * Installable-app support: registers the service worker (production builds only), keeps
 * the browser's install prompt so Settings can offer it, and reports when a new version
 * is waiting so the app can offer a reload.
 */

/** Chromium's install prompt event (not in the DOM typings). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface PwaState {
  /** The browser offered an install prompt we can show. */
  canInstall: boolean;
  /** Running as an installed app (home screen / desktop window). */
  installed: boolean;
  /** A new version has been downloaded and is waiting for a reload. */
  updateReady: boolean;
}

let initialized = false;
let installEvent: BeforeInstallPromptEvent | null = null;
let waitingWorker: ServiceWorker | null = null;
let state: PwaState = { canInstall: false, installed: false, updateReady: false };
const listeners = new Set<() => void>();

function set(patch: Partial<PwaState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia("(display-mode: standalone)").matches;
}

function watchForUpdate(registration: ServiceWorkerRegistration) {
  const offer = (worker: ServiceWorker | null) => {
    // Only an update: on first install there is no controller and nothing to reload.
    if (worker && navigator.serviceWorker.controller) {
      waitingWorker = worker;
      set({ updateReady: true });
    }
  };
  offer(registration.waiting);
  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    worker?.addEventListener("statechange", () => {
      if (worker.state === "installed") offer(worker);
    });
  });
}

/** Call once at startup. */
export function initPwa(): void {
  if (initialized) return;
  initialized = true;
  set({ installed: isStandalone() });

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    installEvent = event as BeforeInstallPromptEvent;
    set({ canInstall: true });
  });
  window.addEventListener("appinstalled", () => {
    installEvent = null;
    set({ canInstall: false, installed: true });
  });

  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;

  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading || !waitingWorker) return;
    reloading = true;
    window.location.reload();
  });
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then(watchForUpdate)
      .catch(() => {
        // Not fatal: the site still works online, it just isn't available offline.
      });
  });
}

/** Shows the browser's install dialog. Resolves to true if the user installed the app. */
export async function promptInstall(): Promise<boolean> {
  const event = installEvent;
  if (!event) return false;
  installEvent = null;
  set({ canInstall: false });
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome === "accepted";
}

/** Activates the waiting version; the page reloads once it takes over. */
export function applyUpdate(): void {
  waitingWorker?.postMessage("SKIP_WAITING");
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePwa(): PwaState {
  return useSyncExternalStore(subscribe, () => state);
}
